import { useEffect, useState } from "react";
import {
  addOutlineSectionToDeck,
  ensureDeckOutline,
  regenerateSlideTextFromOutline,
  removeOutlineSectionFromDeck,
  reorderOutlineSectionsInDeck,
  touchContentUpdatedAt,
  updateOutlineSectionInDeck,
  type SurfDeck,
  type SurfOutlineSection,
} from "@vida/surf-core";
import { OutlineSectionsEditor } from "../outline/OutlineSectionsEditor";

type Props = {
  deck: SurfDeck;
  onDeckChange: (deck: SurfDeck) => void;
};

export function OutlineEditorPanel({ deck, onDeckChange }: Props) {
  const [outline, setOutline] = useState<SurfOutlineSection[]>(() => deck.outline ?? []);

  useEffect(() => {
    if (!deck.outline?.length) {
      const ensured = ensureDeckOutline(deck);
      onDeckChange(ensured);
      setOutline(ensured.outline ?? []);
    }
  }, [deck.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (deck.outline) setOutline(deck.outline);
  }, [deck.outline, deck.contentUpdatedAt]);

  const handleSectionsChange = (nextOutline: SurfOutlineSection[]) => {
    if (nextOutline.length < outline.length) {
      const removed = outline.find((s) => !nextOutline.some((n) => n.id === s.id));
      if (removed && window.confirm("Eliminare la sezione e la slide collegata?")) {
        const next = removeOutlineSectionFromDeck(deck, removed.id);
        setOutline(next.outline ?? []);
        onDeckChange(next);
      }
      return;
    }

    if (nextOutline.length > outline.length) {
      const next = addOutlineSectionToDeck(deck);
      setOutline(next.outline ?? []);
      onDeckChange(next);
      return;
    }

    const reordered =
      nextOutline.length > 1 && nextOutline.some((s, i) => s.id !== outline[i]?.id);
    if (reordered) {
      let working = deck;
      for (let i = 0; i < nextOutline.length; i += 1) {
        const wantId = nextOutline[i]!.id;
        const curId = working.outline?.[i]?.id;
        if (wantId === curId) continue;
        const from = working.outline?.findIndex((s) => s.id === wantId) ?? -1;
        if (from >= 0 && from !== i) {
          working = reorderOutlineSectionsInDeck(working, from, i);
        }
      }
      setOutline(nextOutline);
      onDeckChange(touchContentUpdatedAt({ ...working, outline: nextOutline }));
      return;
    }

    let working = deck;
    for (const section of nextOutline) {
      const prev = outline.find((s) => s.id === section.id);
      if (!prev || JSON.stringify(prev) === JSON.stringify(section)) continue;
      working = updateOutlineSectionInDeck(working, section.id, section);
    }
    setOutline(nextOutline);
    onDeckChange(working);
  };

  return (
    <section className="wizard-panel outline-editor-panel">
      <h2>Outline</h2>
      <p className="muted">
        Modifica il testo base di ogni slide. Immagini, background e stile sulla canvas restano
        invariati finché non usi «Rigenera testo slide».
      </p>

      <div className="wizard-actions">
        <button
          type="button"
          onClick={() => {
            const next = addOutlineSectionToDeck(deck);
            setOutline(next.outline ?? []);
            onDeckChange(next);
          }}
        >
          Aggiungi sezione + slide
        </button>
        <button
          type="button"
          onClick={() => onDeckChange(regenerateSlideTextFromOutline(deck))}
        >
          Rigenera tutte le slide da outline
        </button>
      </div>

      <OutlineSectionsEditor
        sections={outline}
        onSectionsChange={handleSectionsChange}
        showVisualHints={false}
        showAddButton={false}
        onRegenerateSection={(sectionId) =>
          onDeckChange(regenerateSlideTextFromOutline(deck, sectionId))
        }
      />
    </section>
  );
}
